import { useState, useEffect } from 'react';
import { useRouteError, useNavigate, Outlet } from 'react-router';

import { EventAntiFloodException, die, on } from '../../eventBus';
import ErrorPage from '../../pages/ErrorPage/ErrorPage';
import { ROUTES } from '../../router/config';

import './ErrorBoundary.scss';

export type ErrorType = number | 'typeError' | 'antiFlood';

const ErrorBoundary: React.FC = () => {
  const [errorState, setErrorState] = useState<{
    hasError: boolean;
    errorType: ErrorType;
    timeout: number;
  }>({
    hasError: false,
    errorType: 'typeError',
    timeout: 0,
  });

  const routerError = useRouteError() as Response;
  const navigate = useNavigate();

  useEffect(() => {
    if (!routerError) return;

    const errorType = routerError.status ? routerError.status : 'typeError';

    setErrorState({
      hasError: true,
      errorType,
      timeout: 0,
    });

    if (
      errorType === 'typeError' &&
      process.env.NODE_ENV !== 'development' &&
      !window.localStorage.getItem('error_boundary_ignore_redirect')
    ) {
      setTimeout(() => {
        navigate(ROUTES.HOME);
      }, 3000);
    }
  }, [routerError, navigate]);

  useEffect(() => {
    on('anti_flood_exception', ({ timeout }: EventAntiFloodException) => {
      setErrorState({
        hasError: true,
        errorType: 'antiFlood',
        timeout,
      });

      setTimeout(() => {
        navigate(ROUTES.HOME);
      }, timeout * 1000);
    });

    return () => {
      die('anti_flood_exception');
    };
  }, [navigate]);

  if (errorState.hasError) {
    return <ErrorPage errorType={errorState.errorType} timeout={errorState.timeout} />;
  }

  return <Outlet />;
};

export default ErrorBoundary;
